// Tipos e regras do modal de Limite de Reposição (Suprimentos).
//
// O Limite é a regra que decide QUANDO pedir, QUANTO pedir, POR QUANTO e COM
// QUE URGÊNCIA. Aqui mora tudo o que não é JSX: a forma do formulário, a
// leitura do que veio do banco e a conta da quantidade sugerida.

/** O contrato que o shell da Configuração já usa para chamar os modais novos. */
export interface LimitModalProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
  /** `createLimit` ou `updateLimit`. O shell entrega; quem decide criar ou
   *  editar continua sendo o uuid, não esta string. */
  modalType?: string;
}

/**
 * Tudo string porque tudo vem de `<input>` — converter na digitação apaga o
 * "0," de quem está no meio de digitar "0,5". A conversão é uma só, na
 * montagem do payload.
 */
export interface FormularioDeLimite {
  lojaUuid: string;
  localDeEstoqueUuid: string;
  skuUuid: string;

  estoqueMinimo: string;
  estoqueMaximo: string;
  pontoReposicao: string;
  estoqueSeguranca: string;

  quantidadeModo: "fixa" | "ateOMaximo";
  quantidadeFixa: string;

  precoModo: "acordadoVigente" | "fixoTravado";
  precoFixo: string;

  modo: "sugerir" | "enviar";
  tetoDeLinhaReais: string;
  janelaDisparoInicio: string;
  janelaDisparoFim: string;
  janelaDisparoDias: string[];
  fornecedorUuid: string;
  fornecedorAlternativoUuid: string;
  prazoParaEscalarHoras: string;
  prazoRespostaHoras: string;
  politicaLembrete: string;

  coberturaAlvoDias: string;
  coberturaAlvoOrigem: "sugeridoPeloFornecedor" | "definidoPorVoce";

  critico: boolean;
  rearmeHoras: string;
  rearmePercentualAbaixo: string;

  alertType: string;
}

export const DIAS_DA_SEMANA = [
  { valor: "seg", rotulo: "Seg" },
  { valor: "ter", rotulo: "Ter" },
  { valor: "qua", rotulo: "Qua" },
  { valor: "qui", rotulo: "Qui" },
  { valor: "sex", rotulo: "Sex" },
  { valor: "sab", rotulo: "Sáb" },
  { valor: "dom", rotulo: "Dom" },
] as const;

export const POLITICAS_DE_LEMBRETE = [
  { valor: "nenhum", rotulo: "Sem lembrete" },
  { valor: "umLembrete", rotulo: "Um lembrete" },
  { valor: "doisLembretes", rotulo: "Dois lembretes" },
] as const;

export const TIPOS_DE_ALERTA = [
  { valor: "email", rotulo: "E-mail" },
  { valor: "whatsapp", rotulo: "WhatsApp" },
  { valor: "both", rotulo: "E-mail e WhatsApp" },
  { valor: "none", rotulo: "Sem alerta" },
] as const;

export function formularioVazio(): FormularioDeLimite {
  return {
    lojaUuid: "",
    localDeEstoqueUuid: "",
    skuUuid: "",
    estoqueMinimo: "",
    estoqueMaximo: "",
    pontoReposicao: "",
    estoqueSeguranca: "",
    quantidadeModo: "ateOMaximo",
    quantidadeFixa: "",
    precoModo: "acordadoVigente",
    precoFixo: "",
    // "sugerir" é o padrão de propósito: o modo "enviar" manda pedido de compra
    // para fora sozinho. Nascer em automático é como uma regra recém-criada,
    // ainda sem ninguém ter conferido os números, viraria pedido real.
    modo: "sugerir",
    tetoDeLinhaReais: "",
    janelaDisparoInicio: "08:00",
    janelaDisparoFim: "18:00",
    janelaDisparoDias: [],
    fornecedorUuid: "",
    fornecedorAlternativoUuid: "",
    prazoParaEscalarHoras: "",
    prazoRespostaHoras: "",
    politicaLembrete: "nenhum",
    coberturaAlvoDias: "",
    coberturaAlvoOrigem: "definidoPorVoce",
    critico: false,
    rearmeHoras: "",
    rearmePercentualAbaixo: "",
    alertType: "email",
  };
}

function texto(valor: any): string {
  return valor === null || valor === undefined ? "" : String(valor);
}

function lista(valor: any): string[] {
  return Array.isArray(valor) ? valor.map((item) => String(item)) : [];
}

/**
 * O JSON cru do limite aberto.
 *
 * ARMADILHA: a linha que o shell entrega como `selectedObject` é a que a
 * DynamicTable monta no case 5, e ela NÃO carrega
 * `replenishmentLimitInfoValue` — só as colunas já achatadas. Ler apenas dela
 * devolvia `{}` em toda edição, e aí modo de ação, modo de quantidade, preço,
 * janela de disparo, fornecedores, cobertura e criticidade voltavam no padrão;
 * salvar por cima apagaria o que estava gravado (inclusive rebaixando um limite
 * "enviar" para "sugerir" sem ninguém pedir).
 */
export function lerValorCruDoLimite(
  selectedObject: any,
  parseInfoValue: (infoValue: string) => any,
  decryptedInfoToken?: any
): any {
  if (!selectedObject) return {};

  if (selectedObject.replenishmentLimitInfoValue) {
    return parseInfoValue(selectedObject.replenishmentLimitInfoValue) || {};
  }

  const uuid =
    selectedObject.limiteUuid ??
    selectedObject.limitUUID ??
    selectedObject.replenishmentLimitInfoUuid;
  if (!uuid) return {};

  const linha = (decryptedInfoToken?.replenishmentLimitInfo ?? []).filter(
    (info: any) => info?.replenishmentLimitInfoUuid === uuid
  )[0];
  if (!linha?.replenishmentLimitInfoValue) return {};

  return parseInfoValue(linha.replenishmentLimitInfoValue) || {};
}

/**
 * Hidrata o formulário com o limite aberto.
 *
 * Duas origens: as colunas que a tabela de Limites já monta (`minimumStock`,
 * `maximumStock`…, ver DynamicTable case 5) e o JSON cru do limite, onde os
 * campos novos moram. Criar abre com `{}` — por isso todo acesso é opcional.
 */
export function lerLimite(
  selectedObject: any,
  parseInfoValue: (infoValue: string) => any,
  decryptedInfoToken?: any
): FormularioDeLimite {
  const padrao = formularioVazio();
  if (!selectedObject) return padrao;

  const cru = lerValorCruDoLimite(
    selectedObject,
    parseInfoValue,
    decryptedInfoToken
  );

  const modo = cru.modo === "enviar" ? "enviar" : "sugerir";
  const quantidadeModo = cru.quantidadeModo === "fixa" ? "fixa" : "ateOMaximo";
  const precoModo =
    cru.precoModo === "fixoTravado" ? "fixoTravado" : "acordadoVigente";
  const coberturaAlvoOrigem =
    cru.coberturaAlvoOrigem === "sugeridoPeloFornecedor"
      ? "sugeridoPeloFornecedor"
      : "definidoPorVoce";

  return {
    lojaUuid: texto(cru.lojaUuid ?? selectedObject.subsidiaryUuid),
    localDeEstoqueUuid: texto(cru.localDeEstoqueUuid),
    skuUuid: texto(cru.skuUuid ?? selectedObject.skuUUID),
    estoqueMinimo: texto(selectedObject.minimumStock ?? cru.estoqueMinimo),
    estoqueMaximo: texto(selectedObject.maximumStock ?? cru.estoqueMaximo),
    pontoReposicao: texto(selectedObject.reorderPoint ?? cru.pontoReposicao),
    estoqueSeguranca: texto(selectedObject.safetyStock ?? cru.estoqueSeguranca),
    quantidadeModo,
    quantidadeFixa: texto(cru.quantidadeFixa),
    precoModo,
    precoFixo: texto(cru.precoFixo),
    modo,
    tetoDeLinhaReais: texto(cru.tetoDeLinhaReais),
    janelaDisparoInicio:
      texto(cru.janelaDisparoInicio) || padrao.janelaDisparoInicio,
    janelaDisparoFim: texto(cru.janelaDisparoFim) || padrao.janelaDisparoFim,
    janelaDisparoDias: lista(cru.janelaDisparoDias),
    fornecedorUuid: texto(cru.fornecedorUuid ?? selectedObject.supplierUUID),
    fornecedorAlternativoUuid: texto(cru.fornecedorAlternativoUuid),
    prazoParaEscalarHoras: texto(cru.prazoParaEscalarHoras),
    prazoRespostaHoras: texto(cru.prazoRespostaHoras),
    politicaLembrete: texto(cru.politicaLembrete) || padrao.politicaLembrete,
    coberturaAlvoDias: texto(cru.coberturaAlvoDias),
    coberturaAlvoOrigem,
    critico: cru.critico === true,
    rearmeHoras: texto(cru.rearmeHoras),
    rearmePercentualAbaixo: texto(cru.rearmePercentualAbaixo),
    alertType: texto(selectedObject.alertType ?? cru.alertType) || padrao.alertType,
  };
}

/**
 * Texto para número. Vazio vira `null`, não `0`: "não informei o estoque
 * máximo" e "o estoque máximo é zero" são coisas diferentes, e `Number("")`
 * responde 0 às duas.
 */
export function numeroOuNulo(valor: string): number | null {
  const limpo = (valor ?? "").trim();
  if (limpo === "") return null;
  const numero = Number(limpo);
  return Number.isFinite(numero) ? numero : null;
}

/** Quantidade é Decimal(18,6): seis casas, e nem uma a mais de lixo binário. */
function seisCasas(valor: number): number {
  return Math.round(valor * 1e6) / 1e6;
}

export interface SugestaoDeQuantidade {
  /** O que o pedido levaria, já arredondado ao múltiplo da embalagem. */
  quantidade: number | null;
  /** Antes de arredondar — a falta pura. */
  falta: number | null;
  /** O saldo usado na conta. Hoje é sempre 0, e a tela precisa dizer isso. */
  saldo: number;
  /** O que já está a caminho. Hoje é sempre 0, mesma razão. */
  emTransito: number;
  /** Múltiplo aplicado; `null` quando não há embalagem informada. */
  embalagem: number | null;
}

/**
 * Quanto pedir no modo "até o máximo": máximo − saldo − em trânsito, subindo
 * ao próximo múltiplo da embalagem (fornecedor não vende meia caixa).
 *
 * ATENÇÃO — saldo e em trânsito NÃO EXISTEM. Os dois dependem do módulo de
 * movimento de estoque, que ainda não foi feito, e por isso entram como 0. Com
 * saldo 0 a conta devolve o máximo inteiro, que é o pedido MAIOR possível.
 * Quem mostrar esse número sem a etiqueta "considera saldo 0" estará dizendo ao
 * usuário que o sistema mediu o estoque dele — e não mediu.
 */
export function calcularSugestao(
  estoqueMaximo: string,
  embalagem: number | null
): SugestaoDeQuantidade {
  const maximo = numeroOuNulo(estoqueMaximo);
  const tamanhoDaEmbalagem = embalagem;
  const saldo = 0;
  const emTransito = 0;

  if (maximo === null) {
    return {
      quantidade: null,
      falta: null,
      saldo,
      emTransito,
      embalagem: tamanhoDaEmbalagem,
    };
  }

  const falta = Math.max(0, seisCasas(maximo - saldo - emTransito));

  if (tamanhoDaEmbalagem === null || tamanhoDaEmbalagem <= 0) {
    return {
      quantidade: falta,
      falta,
      saldo,
      emTransito,
      embalagem: null,
    };
  }

  // `Math.ceil` direto em `falta / embalagem` erra por representação binária:
  // 0.3/0.1 dá 2.9999999999999996 e o teto viraria 3 embalagens onde 3 já era
  // exato. Arredondar a divisão para seis casas antes do teto elimina o caso.
  const embalagens = Math.ceil(seisCasas(falta / tamanhoDaEmbalagem));

  return {
    quantidade: seisCasas(embalagens * tamanhoDaEmbalagem),
    falta,
    saldo,
    emTransito,
    embalagem: tamanhoDaEmbalagem,
  };
}

/**
 * Quantos itens vêm na embalagem deste SKU NESTE fornecedor.
 *
 * A embalagem é fato do fornecedor, não do limite: ela é digitada uma vez na
 * aba "Preços acordados" do Fornecedor. Repetir o campo aqui criaria dois
 * lugares para o mesmo número e, no dia em que divergissem, o pedido sairia
 * arredondado por um valor que o fornecedor não pratica.
 *
 * Devolve `null` quando não há preço acordado VIGENTE HOJE para o par — e aí
 * não há arredondamento, o que a tela diz em texto em vez de fingir múltiplo 1.
 *
 * A vigência é conferida de propósito: o mesmo par fornecedor+SKU tem histórico
 * (o acordo do ano passado continua na lista). Pegar a primeira linha que casa
 * o SKU arredondaria o pedido pela caixa de um acordo que já venceu. Datas em
 * AAAA-MM-DD comparam como texto, sem `new Date` — que reinterpreta como UTC e
 * derruba o dia em fuso negativo.
 */
export function acharEmbalagemAcordada(
  decryptedInfoToken: any,
  fornecedorUuid: string,
  skuUuid: string,
  parseInfoValue: (infoValue: string) => any
): number | null {
  if (!fornecedorUuid || !skuUuid) return null;

  const fornecedor = (decryptedInfoToken?.supplierInfo ?? []).filter(
    (info: any) => info?.supplierInfoUuid === fornecedorUuid
  )[0];
  if (!fornecedor?.supplierInfoValue) return null;

  const cru = parseInfoValue(fornecedor.supplierInfoValue) || {};
  const precos: any[] = Array.isArray(cru.precosAcordados)
    ? cru.precosAcordados
    : [];

  const hoje = hojeEmTexto();
  const acordo = precos.filter((preco) => {
    if (preco?.skuUuid !== skuUuid) return false;
    const inicio = String(preco?.vigenciaInicio ?? "");
    const fim = String(preco?.vigenciaFim ?? "") || "9999-12-31";
    // Linha sem início é acordo sem "a partir de quando": não vale hoje nem
    // nunca, e não pode ditar o múltiplo do pedido.
    if (!inicio) return false;
    return inicio <= hoje && hoje <= fim;
  })[0];

  const embalagem = Number(acordo?.embalagem);

  return Number.isFinite(embalagem) && embalagem > 0 ? embalagem : null;
}

/** Hoje em AAAA-MM-DD, no fuso do usuário (não em UTC). */
function hojeEmTexto(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, "0");
  const dia = String(agora.getDate()).padStart(2, "0");
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/** Número na tela em português, com as seis casas que o campo aceita. */
export function formatarQuantidade(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
}
